import { useState } from 'react'
import type { PropertyType } from '../../types/project'

export interface NewProjectInput {
  name: string
  propertyType: PropertyType
  width: number
  length: number
}

const PROPERTY_TYPES: { id: PropertyType; label: string }[] = [
  { id: 'vivienda', label: 'Vivienda' },
  { id: 'apartamento', label: 'Apartamento' },
  { id: 'oficina', label: 'Oficina' },
  { id: 'otro', label: 'Otro espacio' },
]

export default function NewProjectDialog({
  onCancel,
  onCreate,
}: {
  onCancel: () => void
  onCreate: (input: NewProjectInput) => void
}) {
  const [name, setName] = useState('Proyecto sin título')
  const [propertyType, setPropertyType] = useState<PropertyType>('vivienda')
  const [width, setWidth] = useState(8)
  const [length, setLength] = useState(12)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="w-[440px] rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-[var(--color-ink)]">Nuevo proyecto</h2>
        <p className="mt-1 text-sm text-[var(--color-ink-soft)]">
          Define los datos básicos. Podrás ajustar todo después dentro del editor.
        </p>

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-[var(--color-ink-soft)]">Nombre del proyecto</span>
            <input
              className="numeric-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-[var(--color-ink-soft)]">Tipo de propiedad</span>
            <div className="grid grid-cols-2 gap-2">
              {PROPERTY_TYPES.map((pt) => (
                <button
                  key={pt.id}
                  type="button"
                  onClick={() => setPropertyType(pt.id)}
                  className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                    propertyType === pt.id
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
                      : 'border-[var(--color-border)] text-[var(--color-ink-soft)] hover:border-[var(--color-border-strong)]'
                  }`}
                >
                  {pt.label}
                </button>
              ))}
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-ink-soft)]">Ancho general (m)</span>
              <input
                type="number"
                min={1}
                step={0.5}
                className="numeric-input"
                value={width}
                onChange={(e) => setWidth(Number(e.target.value))}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-[var(--color-ink-soft)]">Largo general (m)</span>
              <input
                type="number"
                min={1}
                step={0.5}
                className="numeric-input"
                value={length}
                onChange={(e) => setLength(Number(e.target.value))}
              />
            </label>
          </div>
          <p className="text-xs text-[var(--color-ink-soft)]">
            Estas dimensiones son solo una referencia inicial del lote; el plano se dibuja libremente dentro
            del editor y puede exceder o no llenar este límite.
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-muted)]"
          >
            Cancelar
          </button>
          <button
            onClick={() => onCreate({ name: name.trim() || 'Proyecto sin título', propertyType, width, length })}
            className="rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            Crear y abrir editor
          </button>
        </div>
      </div>
    </div>
  )
}
